import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { StarIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutline } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { toast } from 'react-toastify';
import api from '../services/api';

const RatingModal = ({ isOpen, onClose, contractorId, contractorName, onReviewComplete, currentProjectId }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUserSelectedProject, setHasUserSelectedProject] = useState(false); // 사용자가 직접 선택했는지 추적
  const queryClient = useQueryClient();

  // 평가 가능한 프로젝트 조회
  const { data: projects, isLoading } = useQuery(
    ['reviewableProjects', contractorId],
    () => api.get(`/reviews/contractors/${contractorId}/reviewable`).then(res => res.data),
    {
      enabled: isOpen && !!contractorId
    }
  );

  // currentProjectId가 있으면 자동으로 해당 프로젝트를 선택
  // 또는 프로젝트가 1개만 있으면 자동 선택
  useEffect(() => {
    if (projects && !selectedProject && !hasUserSelectedProject) {
      // currentProjectId가 있으면 해당 프로젝트 선택
      if (currentProjectId) {
        const targetProject = projects.find(p => p.id.toString() === currentProjectId.toString());
        if (targetProject) {
          handleProjectSelect(targetProject);
        }
      } 
      // 프로젝트가 1개만 있으면 자동 선택
      else if (projects.length === 1) {
        handleProjectSelect(projects[0]);
      }
    }
  }, [projects, currentProjectId]); // selectedProject를 의존성에서 제거

  // 평가 생성/수정 mutation
  const reviewMutation = useMutation(
    (data) => {
      if (selectedProject?.hasReview) {
        // 기존 평가 수정
        return api.put(`/reviews/${selectedProject.review_id}`, data);
      } else {
        // 새 평가 생성
        return api.post(`/reviews/projects/${selectedProject.id}`, data);
      }
    },
    {
      onSuccess: () => {
        toast.success(selectedProject?.hasReview ? '평가가 수정되었습니다.' : '평가가 등록되었습니다.');
        queryClient.invalidateQueries(['reviewableProjects', contractorId]);
        queryClient.invalidateQueries(['contractors']);
        queryClient.invalidateQueries(['project', selectedProject?.id]);
        if (onReviewComplete) {
          onReviewComplete();
        }
        handleClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '평가 등록에 실패했습니다.');
      }
    }
  );

  const handleClose = () => {
    setSelectedProject(null);
    setRating(5);
    setComment('');
    setHasUserSelectedProject(false);
    onClose();
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    if (project.hasReview) {
      setRating(project.rating || 5);
      setComment(project.comment || '');
    } else {
      setRating(5);
      setComment('');
    }
  };

  const handleSubmit = () => {
    if (!selectedProject) {
      toast.error('평가할 프로젝트를 선택해주세요.');
      return;
    }
    if (rating === 0) {
      toast.error('별점을 선택해주세요.');
      return;
    }

    setIsSubmitting(true);
    reviewMutation.mutate({ rating, comment });
  };

  if (!isOpen) return null;

  const unreviewedProjects = projects?.filter(p => !p.hasReview) || [];
  const reviewedProjects = projects?.filter(p => p.hasReview) || [];
  
  // 앱 환경 체크
  const isApp = window.ReactNativeWebView || window.webkit?.messageHandlers?.native || window.AndroidInterface;

  return (
    <>
      {/* 백드롭 - 살짝 투명하게 해서 뒤가 보이도록 */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] transition-opacity duration-300"
        onClick={handleClose}
      />
      
      {/* 모달 - 고정 크기 플로팅 카드 */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none p-4">
        <div 
          className={`
            pointer-events-auto
            bg-white 
            rounded-2xl 
            shadow-2xl 
            flex flex-col
            transition-opacity duration-300 ease-out
            overflow-hidden
            
            /* 모바일: 고정 크기 - 상하단 메뉴 보임 */
            w-[calc(100%-20px)]
            max-w-[calc(100%-20px)]
            ${isApp ? 'h-[calc(100vh-100px)]' : 'h-[calc(100vh-120px)]'}
            
            /* PC: 중앙 정렬 with 고정 크기 */
            sm:max-w-lg
            sm:w-full
            sm:h-auto
            sm:max-h-[75vh]
            
            /* 위치 조정 */
            ${isApp ? 'mt-[50px] mb-[50px]' : 'mt-[60px] mb-[60px]'}
            sm:mt-0
            sm:mb-0
          `}
        >
          {/* 헤더 */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">소장님 평가하기</h2>
                <p className="text-blue-100 text-sm mt-1">{contractorName} 소장님</p>
              </div>
              <button
                onClick={handleClose}
                className="text-white/80 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* 내용 */}
          <div className="p-6 overflow-y-auto flex-1">
            {isLoading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500">프로젝트 목록을 불러오는 중...</p>
              </div>
            ) : !selectedProject ? (
              // 프로젝트 선택 화면
              <div>
              <p className="text-sm text-gray-600 mb-4">평가할 프로젝트를 선택해주세요.</p>
              
              {unreviewedProjects.length > 0 && (
                <>
                  <h3 className="font-medium text-gray-900 mb-3">평가하지 않은 프로젝트</h3>
                  <div className="space-y-2 mb-6">
                    {unreviewedProjects.map((project) => (
                      <button
                        key={project.id}
                        onClick={() => handleProjectSelect(project)}
                        className="w-full text-left p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors border border-blue-200"
                      >
                        <div className="font-medium text-gray-900">{project.title}</div>
                        <div className="text-sm text-gray-600 mt-1">
                          완료일: {new Date(project.updated_at).toLocaleDateString()}
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}

              {reviewedProjects.length > 0 && (
                <>
                  <h3 className="font-medium text-gray-900 mb-3">이미 평가한 프로젝트</h3>
                  <div className="space-y-2">
                    {reviewedProjects.map((project) => (
                      <button
                        key={project.id}
                        onClick={() => handleProjectSelect(project)}
                        className="w-full text-left p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-gray-900">{project.title}</div>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <StarIcon
                                key={i}
                                className={`h-4 w-4 ${
                                  i < project.rating ? 'text-yellow-400' : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          평가일: {new Date(project.review_created_at).toLocaleDateString()}
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}

              {projects?.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500">평가 가능한 프로젝트가 없습니다.</p>
                  <p className="text-sm text-gray-400 mt-2">
                    완료된 프로젝트만 평가할 수 있습니다.
                  </p>
                </div>
              )}
              </div>
            ) : (
              // 평가 입력 화면
              <div>
              {/* 프로젝트가 여러 개일 때만 목록으로 가기 버튼 표시 */}
              {projects && projects.length > 1 && (
                <button
                  onClick={() => {
                    setSelectedProject(null);
                    setHasUserSelectedProject(true); // 사용자가 직접 목록으로 돌아감
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700 mb-4 flex items-center gap-1"
                >
                  ← 프로젝트 목록으로
                </button>
              )}
              
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-gray-900">{selectedProject.title}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedProject.address}
                </p>
              </div>

              {/* 별점 선택 */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  평점을 선택해주세요
                </label>
                <div className="flex items-center justify-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="focus:outline-none transition-transform hover:scale-110"
                    >
                      {star <= (hoverRating || rating) ? (
                        <StarIcon className="h-10 w-10 text-yellow-400" />
                      ) : (
                        <StarOutline className="h-10 w-10 text-gray-300" />
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-center text-sm text-gray-600 mt-2">
                  {rating > 0 && `${rating}점`}
                </p>
              </div>

              {/* 코멘트 입력 */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  평가 내용 (선택사항)
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="소장님과의 작업 경험을 공유해주세요..."
                />
              </div>
              </div>
            )}
          </div>

        {/* 하단 버튼 */}
        {selectedProject && (
          <div className="p-5 border-t bg-gray-50">
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                취소
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || rating === 0}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isSubmitting ? '처리 중...' : selectedProject.hasReview ? '평가 수정' : '평가 등록'}
              </button>
            </div>
          </div>
        )}
        </div>
      </div>
    </>
  );
};

export default RatingModal;